<template>
  <div class="gmf-notification-list gmf-context">
    <SocialBannerMini v-if="lootCrateBanner" :banner="lootCrateBanner" style="overflow: hidden;" />
    <EventRow v-for="evt in events" :evt="evt" :key="evt.id" @notificationNavigation="$emit('notificationNavigation')" />
    <div v-if="! events.length" class="gmf-text-small" style="text-align: center;" v-text="t('no_notifications')"></div>
    <div ref="scrollObserver" />
  </div>
</template>

<style>
.gmf-notification-list {
  display: flex;
  flex-direction: column;

  width: 100%;
}
</style>

<script>
import state from '@/state';
import EventRow from './EventRow';
import callApi from '@/utils/callApi';
import advancedString from '@/utils/advancedString';
import openTokenShop from '@/components/TokenShop/openTokenShop';
import SocialBannerMini from '@/components/SocialBannerMini';
import openLootCrate from '@/widgets/LootCrate/openLootCrate';

export default {
  emits: ['notificationNavigation'],
  name: 'Notifications/List',
  components: {
    EventRow,
    SocialBannerMini,
  },
  mounted () {
    this.cursor = undefined;
    this.events = [];
    this.loadMoreNotifications();

    this.infinityScroll = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        this.loadMoreNotifications();
      }
    }, {
      rootMargin: '100px'
    });
    this.infinityScroll.observe(this.$refs.scrollObserver);

    this.loadLootCrateStatus();
  },
  data () {
    return {
      state,
      events: [],
      cursor: undefined,
      autoLoadObserver: null,
      lootCrates: [],
    };
  },
  watch: {
    'state.profile': function (p) {
      if (! p) {
        return;
      }
      this.cursor = undefined;
      this.events = [];
      this.loadMoreNotifications();
    }
  },
  methods: {
    t: state.t.prefix('notifications'),
    async loadMoreNotifications () {
      if (! state.profile) {
        return;
      }
      if (this.isLoading) {
        return;
      }
      if (this.cursor !== undefined && ! this.cursor) {
        // Falsy, but defined value means no more events are available
        return;
      }
      this.isLoading = true;
      let url = '/v1/client/notifications?take=8';
      if (this.cursor) {
        url += '&cursor=' + this.cursor;
      }
      const res = await callApi('GET', url);
      const body = await res.json();

      this.cursor = body.nextCursor;
      this.events.push(...body.events);

      this.$nextTick(() => {
        this.isLoading = false;
        
        // if scrollObserver is still in view, load more
        const scrollObserver = this.$refs.scrollObserver;
        if (scrollObserver) {
          const rect = scrollObserver.getBoundingClientRect();
          if (rect.top < window.innerHeight) {
            this.loadMoreNotifications();
          }
        }
      });
    },
    async loadLootCrateStatus() {
      const lcRes = await callApi('GET', '/v3/customer-loot-crates-status');
      const lcBody = await lcRes.json();
      this.lootCrates = lcBody;
    },
  },
  computed: {
    showTokenShop() {
      return state.merchantConfig.tokenShop?.enabled;
    },
    lootCrateBanner() {
      const lootCratesToOpen = this.lootCrates.filter(lc => {
        if (! lc.quantity || lc.quantity <= 0) {
          return false;
        }
        return true;
      });

      if (lootCratesToOpen.length > 0) {
        const lc = lootCratesToOpen[0];
        const b = {
          imageType: 'lootCrate',
          imageUrl: lc.beforeOpenImageUrl || lc.iconUrl || lc.openImageUrl,
          buttonText: state.t('loot_crates.open_btn'),
          text: advancedString(state.t('loot_crates.new_loot_in_inventory'), {
            lootCrateName: lc.name,
          }),
          quantity: lc.quantity,
          onClick: async () => {
            await openLootCrate(lc);
            this.loadLootCrateStatus();
          }
        };
        return b;
      }

      const now = new Date();
      const availableLootCrates = this.lootCrates.filter(lc => {
        if (parseInt(lc.nextOpenTime) !== 0) {
          return false;
        }
        let hasValidPrice = false;
        for (const price of lc.prices) {
          if (price.endsAt && new Date(price.endsAt) <= now) {
            continue;
          }
          if (price.startsAt && new Date(price.startsAt) > now) {
            continue;
          }
          hasValidPrice = true;
          break;
        }
        if (!hasValidPrice) {
          return false;
        }
        return true;
      });
      if (availableLootCrates.length === 0) {
        return null;
      }
      if (! this.showTokenShop) {
        return null;
      }
      const lc = availableLootCrates[0];
      const b = {
        imageType: 'lootCrate',
        imageUrl: lc.beforeOpenImageUrl || lc.iconUrl || lc.openImageUrl,
        buttonText: state.t('loot_crates.open_btn'),
        text: advancedString(state.t('loot_crates.new_loot_available'), {
          lootCrateName: lc.name,
        }),
        onClick: openTokenShop,
      };
      return b;
    },
  }
};
</script>
